import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CompanyUserAvatar } from "@/components/user/company-user-avatar";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Loader2, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { urlFor } from "~/client";

type AssigneeOption = { id: string; name: string; avatar: string | null };
type AssigneesResponse = { users: AssigneeOption[] };

const loadAssignees = async (miniTaskId: string, signal?: AbortSignal) => {
  const response = await axios.get<AssigneesResponse>(
    urlFor("mini-tasks.assignees", { miniTaskId: Number(miniTaskId) }),
    { signal },
  );
  return response.data.users ?? [];
};

/**
 * Picker de "pasar a otra persona" (reasignar). Carga los miembros del equipo de
 * la mini-tarea bajo demanda (al abrir) y delega la mutación al padre vía
 * `onReassign` (que tiene el contexto + refresco realtime). Excluye al asignado
 * actual para no reasignarse a sí mismo.
 */
export function ReassignMiniTask({
  miniTaskId,
  open,
  onOpenChange,
  excludeUserId,
  onReassign,
}: {
  miniTaskId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  excludeUserId?: string | null;
  onReassign: (toUserId: string) => Promise<void>;
}) {
  const { t } = useTranslation("chat");
  const [search, setSearch] = useState("");
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  const assigneesQuery = useQuery({
    queryKey: ["mini-task-assignees", miniTaskId],
    queryFn: ({ signal }) => loadAssignees(miniTaskId, signal),
    enabled: open,
    staleTime: 60_000,
  });

  const candidates = useMemo(() => {
    const users = assigneesQuery.data ?? [];
    const term = search.trim().toLowerCase();
    return users
      .filter((user) => user.id !== excludeUserId)
      .filter((user) => (term ? user.name.toLowerCase().includes(term) : true));
  }, [assigneesQuery.data, excludeUserId, search]);

  const handlePick = async (toUserId: string) => {
    setSubmittingId(toUserId);
    try {
      await onReassign(toUserId);
      onOpenChange(false);
      setSearch("");
    } catch {
      // En error mantenemos el picker abierto para reintentar; el toast lo emite
      // el `onError` centralizado del provider. Solo cerramos en éxito.
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("mini_tasks.reassign.title", "Pasar a otra persona")}</DialogTitle>
          <DialogDescription>
            {t("mini_tasks.reassign.description", "Elige a quién delegar esta mini-tarea.")}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("mini_tasks.reassign.search", "Buscar persona...")}
            className="pl-9"
          />
        </div>

        <div className="max-h-72 space-y-0.5 overflow-y-auto">
          {assigneesQuery.isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-8 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("mini_tasks.reassign.loading", "Cargando personas...")}
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center text-sm">
              {t("mini_tasks.reassign.empty", "No hay personas disponibles.")}
            </div>
          ) : (
            candidates.map((user) => (
              <button
                key={user.id}
                type="button"
                disabled={submittingId !== null}
                onClick={() => void handlePick(user.id)}
                className="hover:bg-accent flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors disabled:opacity-60"
              >
                <CompanyUserAvatar
                  userId={Number(user.id)}
                  name={user.name}
                  avatar={user.avatar}
                  className="h-8 w-8 text-xs"
                />
                <span className="min-w-0 flex-1 truncate">{user.name}</span>
                {submittingId === user.id && <Loader2 className="h-4 w-4 animate-spin" />}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
